import React from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import InputBase from "@material-ui/core/InputBase";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Drawer from "@material-ui/core/Drawer";
import Divider from "@material-ui/core/Divider";
import AddIcon from "@material-ui/icons/Add";
import Search from "@material-ui/icons/Search";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Close from "@material-ui/icons/Close";
import Receipt from "@material-ui/icons/Receipt";
import AddShoppingCart from "@material-ui/icons/AddShoppingCart";

// @material-ui/icons
import Assignment from "@material-ui/icons/Assignment";
import Edit from "@material-ui/icons/Edit";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Table from "components/Table/Table.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";
import EditCustomerDrawer from "./EditCustomerDrawer.js";
import Snackbar from "components/Snackbar/Snackbar.js";
import CheckCircle from "@material-ui/icons/CheckCircle";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const useStyles = makeStyles(styles);
const useCustomStyles = makeStyles({
  phoneCell: {
    maxWidth: "150px",
    width: "150px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

const useDrawerStyles = makeStyles({
  paper: { width: 480, display: "flex", flexDirection: "column" },
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    padding: "16px 20px 12px",
  },
  body: { flex: 1, overflowY: "auto", padding: "0 20px" },
  footer: { padding: "12px 20px", borderTop: "1px solid #e0e0e0" },
});

const INV_STATUS_COLORS = {
  Processing:          { bg: "#fff3e0", color: "#e65100" },
  Paid:                { bg: "#e8eaf6", color: "#3949ab" },
  Shipped:             { bg: "#e0f7fa", color: "#00838f" },
  Completed:           { bg: "#e8f5e9", color: "#2e7d32" },
  Returned:            { bg: "#fce4ec", color: "#ad1457" },
  Cancelled:           { bg: "#f5f5f5", color: "#757575" },
  "Shipment returned": { bg: "#e3f2fd", color: "#0d47a1" },
};

const CUSTOMER_INVOICES_QUERY = `
  query GetCustomerInvoices($customerId: String!, $page: Int!, $pageSize: Int!) {
    invoicesWithPagination(filter: { customerId: $customerId, page: $page, pageSize: $pageSize, isReturn: false }) {
      data { id invoiceNumber invoiceDate status total paid }
      total
    }
  }
`;

function formatNum(val) {
  if (val == null) return "0";
  return Number(val).toLocaleString("vi-VN");
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const CUSTOMERS_QUERY = `
  query GetCustomers($page: Int!, $pageSize: Int!, $search: String) {
    customersWithPagination(filter: { page: $page, pageSize: $pageSize, search: $search }) {
      data {
        id
        customerNumber
        name
        phone1
        isActive
        currentDebitAmount
        totalPurchase
        totalPurchaseExcludeReturns
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

function formatNumber(val) {
  if (val == null) return "0";
  return Number(val).toLocaleString("vi-VN");
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function CustomersPage() {
  const classes = useStyles();
  const custom = useCustomStyles();
  const drawerClasses = useDrawerStyles();
  const history = useHistory();

  const [customers, setCustomers] = React.useState([]);
  const [editCustomerId, setEditCustomerId] = React.useState(null);

  // Invoice drawer state
  const [invDrawer, setInvDrawer] = React.useState({ open: false, customer: null });
  const [custInvoices, setCustInvoices] = React.useState([]);
  const [custInvTotal, setCustInvTotal] = React.useState(0);
  const [custInvLoading, setCustInvLoading] = React.useState(false);

  const openInvDrawer = (customer) => {
    setInvDrawer({ open: true, customer });
    setCustInvoices([]);
    setCustInvLoading(true);
    const token = localStorage.getItem("token");
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: token ? `Bearer ${token}` : "" },
      credentials: "include",
      body: JSON.stringify({ query: CUSTOMER_INVOICES_QUERY, variables: { customerId: customer.id, page: 1, pageSize: 50 } }),
    })
      .then((r) => r.json())
      .then(({ data }) => {
        setCustInvoices(data?.invoicesWithPagination?.data || []);
        setCustInvTotal(data?.invoicesWithPagination?.total || 0);
      })
      .finally(() => setCustInvLoading(false));
  };
  const [notify, setNotify] = React.useState({ open: false, message: "" });
  const showNotify = (message) => {
    setNotify({ open: true, message });
    setTimeout(() => setNotify((n) => ({ ...n, open: false })), 4000);
  };
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [pageInput, setPageInput] = React.useState("1");
  const [search, setSearch] = React.useState("");
  const searchRef = React.useRef("");
  const debounceRef = React.useRef(null);

  const fetchCustomers = React.useCallback((pg, ps, srch) => {
    setLoading(true);
    setError("");
    const token = localStorage.getItem("token");
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      credentials: "include",
      body: JSON.stringify({
        query: CUSTOMERS_QUERY,
        variables: { page: pg, pageSize: ps, search: srch || undefined },
      }),
    })
      .then((res) => res.json())
      .then(({ data, errors }) => {
        if (errors) {
          setError(errors[0].message);
        } else {
          const p = data.customersWithPagination;
          setCustomers(p.data);
          setTotal(p.total);
          setTotalPages(p.totalPages);
          setPage(p.page);
          setPageInput(String(p.page));
        }
      })
      .catch(() => setError("Không thể kết nối máy chủ"))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    fetchCustomers(1, pageSize, "");
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    searchRef.current = val;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCustomers(1, pageSize, val);
    }, 400);
  };

  const handlePageSizeChange = (e) => {
    const ps = e.target.value;
    setPageSize(ps);
    fetchCustomers(1, ps, searchRef.current);
  };

  const goToPage = (pg) => {
    if (pg < 1 || pg > totalPages) return;
    fetchCustomers(pg, pageSize, searchRef.current);
  };

  const handlePageInputKey = (e) => {
    if (e.key === "Enter") {
      const pg = parseInt(pageInput, 10);
      if (!isNaN(pg)) goToPage(pg);
    }
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const tableData = customers.map((c) => [
    <span key={c.id} style={{ display: "inline-flex", gap: "2px" }}>
      <IconButton size="small" onClick={() => setEditCustomerId(c.id)}>
        <Edit fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={() => openInvDrawer(c)} title="View invoices">
        <Receipt fontSize="small" style={{ color: "#1565c0" }} />
      </IconButton>
    </span>,
    c.customerNumber || "-",
    <span key={c.id + "-name"} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      {c.name}
      {!c.isActive && (
        <span style={{ fontSize: "11px", fontWeight: 600, padding: "1px 7px", borderRadius: "10px", backgroundColor: "#f5f5f5", color: "#9e9e9e", border: "1px solid #e0e0e0", whiteSpace: "nowrap" }}>
          Inactive
        </span>
      )}
    </span>,
    c.phone1 || "-",
    formatNumber(c.currentDebitAmount),
    formatNumber(c.totalPurchase),
    formatNumber(c.totalPurchaseExcludeReturns),
  ]);

  return (
    <>
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <Assignment />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Customers</h4>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setEditCustomerId("new")}
              style={{ position: "absolute", right: "16px", top: "16px" }}
            >
              Add new customer
            </Button>
          </CardHeader>
          <CardBody>
            {error && (
              <p style={{ color: "#f44336", textAlign: "center" }}>{error}</p>
            )}

            {/* Search bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px 10px",
                marginBottom: "12px",
                maxWidth: "360px",
              }}
            >
              <Search style={{ color: "#aaa", marginRight: "6px", fontSize: "20px" }} />
              <InputBase
                placeholder="Search by name, code or phone..."
                value={search}
                onChange={handleSearchChange}
                style={{ flex: 1, fontSize: "14px" }}
              />
            </div>

            <Table
              tableHead={[
                "",
                "Code",
                "Name",
                "Phone number",
                "Debit amount",
                "Total purchase",
                "Final total (ex. returns)",
              ]}
              tableData={loading ? [] : tableData}
              customCellClasses={[
                classes.center,
                custom.phoneCell,
                classes.right,
                classes.right,
                classes.right,
              ]}
              customClassesForCells={[0, 3, 4, 5, 6]}
              customHeadCellClasses={[
                classes.center,
                custom.phoneCell,
                classes.right,
                classes.right,
                classes.right,
              ]}
              customHeadClassesForCells={[0, 3, 4, 5, 6]}
            />
            {loading && (
              <p style={{ textAlign: "center", padding: "16px 0" }}>
                Loading...
              </p>
            )}

            {/* Pagination bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "8px",
                padding: "8px 0",
                flexWrap: "wrap",
                fontSize: "14px",
              }}
            >
              {/* Show X rows */}
              <span>Show</span>
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                disableUnderline
                style={{
                  fontSize: "14px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "2px 8px",
                }}
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt} rows
                  </MenuItem>
                ))}
              </Select>

              {/* First */}
              <IconButton
                size="small"
                onClick={() => goToPage(1)}
                disabled={page <= 1}
              >
                <FirstPage fontSize="small" />
              </IconButton>

              {/* Prev */}
              <IconButton
                size="small"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>

              {/* Page input */}
              <input
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={handlePageInputKey}
                onBlur={() => {
                  const pg = parseInt(pageInput, 10);
                  if (!isNaN(pg)) goToPage(pg);
                  else setPageInput(String(page));
                }}
                style={{
                  width: "48px",
                  textAlign: "center",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "4px 0",
                  fontSize: "14px",
                }}
              />

              {/* Next */}
              <IconButton
                size="small"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
              >
                <ChevronRight fontSize="small" />
              </IconButton>

              {/* Last */}
              <IconButton
                size="small"
                onClick={() => goToPage(totalPages)}
                disabled={page >= totalPages}
              >
                <LastPage fontSize="small" />
              </IconButton>

              {/* Summary */}
              <span style={{ color: "#555" }}>
                {from} – {to} of {total.toLocaleString("vi-VN")} customers
              </span>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>

    {/* ── Customer invoice drawer ── */}
    <Drawer
      anchor="right"
      open={invDrawer.open}
      onClose={() => setInvDrawer({ open: false, customer: null })}
      classes={{ paper: drawerClasses.paper }}
    >
      {/* Header */}
      <div className={drawerClasses.header}>
        <div>
          <div style={{ fontWeight: 700, fontSize: "16px", color: "#1a1a1a" }}>
            {invDrawer.customer?.name}
          </div>
          <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
            {custInvLoading ? "Loading..." : `${custInvTotal} invoice${custInvTotal !== 1 ? "s" : ""}`}
          </div>
        </div>
        <IconButton size="small" onClick={() => setInvDrawer({ open: false, customer: null })}>
          <Close fontSize="small" />
        </IconButton>
      </div>
      <Divider />

      {/* Invoice list */}
      <div className={drawerClasses.body}>
        {custInvLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>Loading...</div>
        ) : custInvoices.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa", fontSize: "14px" }}>
            No invoices found
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "8px" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, color: "#555" }}>Invoice</th>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, color: "#555" }}>Date</th>
                <th style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, color: "#555" }}>Status</th>
                <th style={{ textAlign: "right", padding: "6px 8px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, color: "#555" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {custInvoices.map((inv) => {
                const sc = INV_STATUS_COLORS[inv.status] || { bg: "#f5f5f5", color: "#555" };
                const hasDebt = (inv.total - inv.paid) > 0;
                return (
                  <tr key={inv.id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                    <td style={{ padding: "8px 8px" }}>
                      <span
                        style={{ color: "#1565c0", fontWeight: 600, cursor: "pointer", textDecoration: "underline" }}
                        onClick={() => { history.push(`/admin/invoices/${inv.id}`); setInvDrawer({ open: false, customer: null }); }}
                      >
                        {inv.invoiceNumber}
                      </span>
                    </td>
                    <td style={{ padding: "8px 8px", color: "#555" }}>{formatDate(inv.invoiceDate)}</td>
                    <td style={{ padding: "8px 8px" }}>
                      <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 700, backgroundColor: sc.bg, color: sc.color }}>
                        {inv.status}
                      </span>
                    </td>
                    <td style={{ padding: "8px 8px", textAlign: "right" }}>
                      <div style={{ fontWeight: 600 }}>{formatNum(inv.total)}</div>
                      {hasDebt && (
                        <div style={{ fontSize: "11px", color: "#f44336" }}>
                          owe {formatNum(inv.total - inv.paid)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Divider />
      {/* Footer */}
      <div className={drawerClasses.footer}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<AddShoppingCart />}
          onClick={() => {
            setInvDrawer({ open: false, customer: null });
            history.push(`/admin/invoices/new?customerId=${invDrawer.customer?.id}&customerName=${encodeURIComponent(invDrawer.customer?.name || "")}`);
          }}
        >
          Create Invoice
        </Button>
      </div>
    </Drawer>

    <EditCustomerDrawer
      customerId={editCustomerId}
      onClose={() => setEditCustomerId(null)}
      onSaved={(msg) => {
        showNotify(msg || "Customer saved successfully!");
        fetchCustomers(page, pageSize, searchRef.current);
      }}
    />
    <Snackbar
      place="tr"
      color="success"
      icon={CheckCircle}
      message={notify.message}
      open={notify.open}
      closeNotification={() => setNotify((n) => ({ ...n, open: false }))}
      close
    />
    </>
  );
}
